import React, { useState } from "react";
import { KeyRound, Plus, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "./ssoFormat";
import { PasswordField } from "../PasswordField";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

const blank = { username: "", display_name: "", password: "", role: "system_admin" };

function IdentityDialog({ mode, identity, roles, onClose, onSubmit }) {
  const [form, setForm] = useState(blank);
  const creating = mode === "create";
  const ready = form.password.length >= 12 && (!creating || (form.username && form.display_name.length >= 2));
  return (
    <Dialog open={Boolean(mode)} onOpenChange={(next) => { if (!next) { setForm(blank); onClose(); } }}>
      <DialogContent data-testid="identity-dialog">
        <DialogHeader>
          <DialogTitle>{creating ? "New emergency identity" : `Reset password for ${identity?.username}`}</DialogTitle>
          <DialogDescription>
            Emergency identities are separate from normal accounts and only work while emergency
            access is switched on. Store the password offline with your incident runbook.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 my-4">
          {creating && (
            <>
              <label className="block"><span className="field-label">Username</span>
                <input className="input-flat" value={form.username} placeholder="e.g. bg-ops-1" onChange={(e) => setForm({ ...form, username: e.target.value.toLowerCase() })} data-testid="identity-username" /></label>
              <label className="block"><span className="field-label">Display name</span>
                <input className="input-flat" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="identity-display-name" /></label>
              <label className="block"><span className="field-label">Role during an emergency</span>
                <select className="input-flat" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="identity-role">
                  {roles.map((role) => <option key={role} value={role}>{role.replace(/_/g, " ")}</option>)}
                </select></label>
            </>
          )}
          <div><span className="field-label">Password (12+ characters, upper, lower, number, symbol)</span>
            <PasswordField minLength={12} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="identity-password" /></div>
        </div>
        <DialogFooter>
          <button className="btn-ghost" onClick={() => { setForm(blank); onClose(); }} data-testid="identity-cancel">Cancel</button>
          <button className="btn-primary" disabled={!ready} onClick={() => { onSubmit(form); setForm(blank); }} data-testid="identity-continue">Continue</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EmergencyIdentitiesCard({ data, onStepUp, onChanged }) {
  const [dialog, setDialog] = useState({ mode: "", identity: null });
  const { identities, roles, limits, viewer_is_emergency: readOnly } = data;

  const withStepUp = (title, call, success) => onStepUp({
    purpose: "breakglass", title,
    onConfirm: async (grant) => { await call(grant); toast.success(success); onChanged(); },
  });
  const submit = (form) => {
    const { mode, identity } = dialog;
    setDialog({ mode: "", identity: null });
    if (mode === "create") withStepUp("Confirm new emergency identity", (grant) => api.post("/admin/breakglass/identities", { ...form, grant }), "Emergency identity created");
    else withStepUp("Confirm password reset", (grant) => api.patch(`/admin/breakglass/identities/${identity.id}`, { password: form.password, grant }), "Password reset");
  };
  const toggle = (identity) => withStepUp(identity.active ? "Confirm deactivation" : "Confirm activation",
    (grant) => api.patch(`/admin/breakglass/identities/${identity.id}`, { active: !identity.active, grant }),
    identity.active ? "Identity deactivated" : "Identity activated");
  const remove = async (identity) => {
    if (!window.confirm(`Remove ${identity.username}? Its sessions end immediately.`)) return;
    try { await api.delete(`/admin/breakglass/identities/${identity.id}`); toast.success("Identity removed"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="identities-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="eyebrow mb-1">Emergency-only identities</div>
          <h3 className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>Local breakglass accounts</h3>
        </div>
        {!readOnly && (
          <button className="btn-ghost flex items-center gap-2 text-sm" disabled={identities.length >= limits.max_identities} onClick={() => setDialog({ mode: "create", identity: null })} data-testid="identity-create"><Plus size={14} /> New identity</button>
        )}
      </div>
      {identities.map((identity) => (
        <div key={identity.id} className="py-3 border-t flex flex-wrap items-center gap-3 text-sm" style={{ borderColor: "var(--border)" }} data-testid={`identity-row-${identity.username}`}>
          <div className="flex-1 min-w-[200px]">
            <span className="font-medium" style={{ color: "var(--ink)" }}>{identity.display_name}</span>
            <span className="font-mono text-xs ml-2" style={{ color: "var(--muted)" }}>{identity.username}</span>
            <div className="text-xs" style={{ color: "var(--muted)" }}>
              {identity.role.replace(/_/g, " ")} · last used {formatWhen(identity.last_login_at)} · password set {formatWhen(identity.password_changed_at, false)}
            </div>
          </div>
          <span className={`pill text-[9px] ${identity.active ? "pill-success" : ""}`} data-testid={`identity-status-${identity.username}`}>{identity.active ? "Active" : "Inactive"}</span>
          {!readOnly && (
            <div className="flex gap-1.5">
              <button className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" onClick={() => setDialog({ mode: "reset", identity })} data-testid={`identity-reset-${identity.username}`}><KeyRound size={12} /> Reset</button>
              <button className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" onClick={() => toggle(identity)} data-testid={`identity-toggle-${identity.username}`}><Power size={12} /> {identity.active ? "Deactivate" : "Activate"}</button>
              <button className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" onClick={() => remove(identity)} data-testid={`identity-remove-${identity.username}`}><Trash2 size={12} /> Remove</button>
            </div>
          )}
        </div>
      ))}
      {!identities.length && <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="identities-empty">No emergency identities yet.</p>}
      <IdentityDialog mode={dialog.mode} identity={dialog.identity} roles={roles} onClose={() => setDialog({ mode: "", identity: null })} onSubmit={submit} />
    </section>
  );
}
